import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { apiFetch } from "./client";
import type {
  Team,
  TeamMember,
  CreateTeamRequest,
  AddMemberRequest,
  UpdateTeamRequest,
} from "@/types/team";

export const teamKeys = {
  all: ["teams"] as const,
  list: () => ["teams", "list"] as const,
  detail: (id: string) => ["teams", id] as const,
  members: (id: string) => ["teams", id, "members"] as const,
  apiKey: (id: string) => ["teams", id, "api-key"] as const,
};

export function useTeamsQuery() {
  return useQuery({
    queryKey: teamKeys.list(),
    queryFn: () => apiFetch<Team[]>("/teams"),
  });
}

export function useTeamByIdQuery(id: string) {
  return useQuery({
    queryKey: teamKeys.detail(id),
    queryFn: () => apiFetch<Team>(`/teams/${id}`),
    enabled: !!id,
  });
}

export function useTeamMembersQuery(id: string) {
  return useQuery({
    queryKey: teamKeys.members(id),
    queryFn: () => apiFetch<TeamMember[]>(`/teams/${id}/members`),
    enabled: !!id,
  });
}

export function useCreateTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateTeamRequest) =>
      apiFetch<Team>("/teams", {
        method: "POST",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useUpdateTeamMutation(id: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateTeamRequest) =>
      apiFetch<{ updated: boolean }>(`/teams/${id}`, {
        method: "PATCH",
        body: JSON.stringify(data),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useDeleteTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ deleted: boolean }>(`/teams/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.all });
    },
  });
}

export function useAddTeamMemberMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: AddMemberRequest) =>
      apiFetch<{ added: boolean; user_id: string; username: string }>(
        `/teams/${teamId}/members`,
        {
          method: "POST",
          body: JSON.stringify(data),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.members(teamId),
      });
    },
  });
}

export function useRemoveTeamMemberMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) =>
      apiFetch<{ removed: boolean }>(
        `/teams/${teamId}/members/${userId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.members(teamId),
      });
    },
  });
}

export function useUpdateMemberRoleMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      role,
    }: {
      userId: string;
      role: string;
    }) =>
      apiFetch<{ updated: boolean }>(
        `/teams/${teamId}/members/${userId}`,
        {
          method: "PATCH",
          body: JSON.stringify({ role }),
        },
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.members(teamId),
      });
    },
  });
}

export function useSetTeamApiKeyMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (apiKey: string) =>
      apiFetch<{ has_key: boolean }>(`/teams/${teamId}/api-key`, {
        method: "PUT",
        body: JSON.stringify({ api_key: apiKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.apiKey(teamId),
      });
    },
  });
}

export function useDeleteTeamApiKeyMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () =>
      apiFetch<{ has_key: boolean }>(`/teams/${teamId}/api-key`, {
        method: "DELETE",
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: teamKeys.apiKey(teamId),
      });
    },
  });
}

export function useTeamApiKeyStatusQuery(teamId: string) {
  return useQuery({
    queryKey: teamKeys.apiKey(teamId),
    queryFn: () =>
      apiFetch<{ has_key: boolean }>(`/teams/${teamId}/api-key`),
    enabled: !!teamId,
  });
}

export function useSetActiveTeamMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (teamId: string) =>
      apiFetch<{ active_team_id: string }>("/auth/active-team", {
        method: "PUT",
        body: JSON.stringify({ team_id: teamId }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["auth"] });
    },
  });
}

export function useSetMemberTeamKeyMutation(teamId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, useTeamKey }: { userId: string; useTeamKey: boolean }) =>
      apiFetch(`/teams/${teamId}/members/${userId}/team-key`, {
        method: "PUT",
        body: JSON.stringify({ use_team_key: useTeamKey }),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamKeys.members(teamId) });
    },
  });
}
